import { CellClassParams, ColDef, GetContextMenuItemsParams, GridOptions, RowNode, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { defaultPriceCellFormatter, centeredColumnDef, defaultLoadingOverlayTemplate, getMasterSymbolColumn } from 'projects/shared-components/ag-grid-contrib';
import { PortfolioItemType } from 'projects/shared-components/portfolios/portfolios.model';
import { PortfolioItemDto } from 'projects/shared-components/shell-communication/shell-dto-protocol';
import { StrategyState } from 'projects/shared-components/strategies/strategy-state.enum';
import { isCashSettledOptionTicker } from 'projects/shared-components/utils';
import { isNullOrUndefined } from 'util';
import { OrderDestination } from '../multi-trade-pad.model';
import { MultiLegOrderComponent } from './multi-leg-order.component';

export function getBucketReferenceGridOptions(this: MultiLegOrderComponent, dest: OrderDestination): GridOptions {

   const gridOptions: GridOptions = {
      debug: true,

      rowData: [],

      defaultColDef: centeredColumnDef,
      
      columnDefs: getColumnDefs(this),

      overlayLoadingTemplate: defaultLoadingOverlayTemplate,
      
      immutableData: true,

      rowClass: 'ets-text-centered',
      
      rowSelection: 'multiple',

      rowGroupPanelShow: 'never',

      groupUseEntireRow: false,

      suppressAggFuncInHeader: true,

      groupIncludeTotalFooter: true,

      groupIncludeFooter: true,

      tooltipShowDelay: 1000,
      
      onGridReady: (args) => {
         dest.onGridReady(args);
      },

      getRowNodeId: (data: PortfolioItemDto) => {
         return data.portfolioItemId;
      },

      postSort: (rowNodes: RowNode[]) => {

         const nextInsertPos = rowNodes.length;
         for (let i = rowNodes.length - 1; i >= 0; i--) {
             const pfItem: PortfolioItemDto = rowNodes[i].data;
             if (pfItem && pfItem.isHedgingItem) {
                if (i !== nextInsertPos) {
                   rowNodes.splice(nextInsertPos, 0, rowNodes.splice(i, 1)[0]);
                }
             }
         }
      },

      onFirstDataRendered: (args) => {
         args.columnApi.autoSizeAllColumns();
      },
      
      getContextMenuItems: (args: GetContextMenuItemsParams) => {
      
         if (!args.node) {
            return;
         }

         if (args.node.group) {
            return;
         }

         const pfItem = args.node.data as PortfolioItemDto;
         
         if (!pfItem) {
            return;
         }

         const menu = [];

         menu.push(
            'separator',
            {
               name: 'Size To Fit',
               action: () => args.api.sizeColumnsToFit()
            },
            'autoSizeAll',
         );

         return menu;
      },
   };

   return gridOptions;
}


function getColumnDefs(comp: MultiLegOrderComponent): Partial<ColDef>[] {

   const colDefs: ColDef[] = [];

   const symbolCol = getMasterSymbolColumn('ticker', comp.messageBus, comp.timestampsService, comp.unsubscriber, () => comp.gridApi);

   const itemTypeCol: ColDef = {
      headerName: 'Type',
      field: 'itemType',
      sort: 'asc',
      sortIndex: 2,
      valueFormatter: (args: ValueFormatterParams) => {
         if (!args.data) {
            return args.value;
         }

         if (args.value > 0) {
            let optType = PortfolioItemType[args.value];
            
            if (args.value >= 3) {
               if (isCashSettledOptionTicker(args.data.ticker)) {
                  optType += ' (E)';
               }
            } 

            return optType;
            
         }

         const data = args.data as PortfolioItemDto;

         if (!data || data.itemType !== PortfolioItemType.Strategy) {
            return PortfolioItemType[args.value];
         }

         return data.algoName;
      },
      cellStyle: (args: CellClassParams) => {
         
         const pfItem = args.data as PortfolioItemDto;

         if (isNullOrUndefined(pfItem)) {
            return undefined;
         }
      


         let state = pfItem.strategyState;

         if (isNullOrUndefined(state)) {
            const strategy = comp.strategyService.getById(pfItem.portfolioItemId);

            if (isNullOrUndefined(strategy)) {
               return undefined;
            }

            state = strategy.state;
         }


         const enabled = (state & StrategyState.Enabled) === state;

         const style = { 'border-bottom': null };

         if (enabled) {
            style['border-bottom'] = '1px solid limegreen';
         } else {
            style['border-bottom'] = '1px solid red';
         }
         
         return style;
      },
      minWidth: 100
   };

   const positionCol: ColDef = {
      headerName: 'Position',
      field: 'netPosition',
      valueGetter: (params: ValueGetterParams) => {
         if (!params.data) {
            return null;
         }
         return params.data.netPosition;
      }
   };

   const avgPxCol: ColDef = {
      headerName: 'Avg. Px',
      field: 'avgPx',
      valueGetter: (params: ValueGetterParams) => {
         if (!params.data || isNaN(params.data.avgPx)) {
            return null;
         }
         return params.data.avgPx;
      },
      valueFormatter: defaultPriceCellFormatter,
   };

   const accountNameCol: ColDef = {
      headerName: 'Account',
      field: 'accountCode',
      minWidth: 180,
      sort: 'asc',
   };

   const openPrice: ColDef = {
      headerName: 'Open Px.',
      field: 'openPrice',
      valueGetter: (params: ValueGetterParams) => {
         if (!params.data || isNaN(params.data.openPrice)) {
            return null;
         }
         return params.data.openPrice;
      },
      valueFormatter: defaultPriceCellFormatter
   };

   colDefs.push(symbolCol);
   colDefs.push(itemTypeCol);
   colDefs.push(positionCol);
   colDefs.push(avgPxCol);
   colDefs.push(openPrice);
   colDefs.push(accountNameCol);

   return colDefs;
}

